


























































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import {
  useCreatePaymentPlan,
  useUpdatePaymentPlan
} from '@/composable/payment-plan';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IPaymentPlan,
  IPaymentPlanData,
  IQuote,
  PaymentPlan,
  QuoteState
} from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import QuotePicker from '@/components/quote-common/QuotePicker.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import DatePicker from '@/components/common/DatePicker.vue';

export default defineComponent({
  name: 'PaymentPlanDialog',
  components: { ALDialog, ErrorAlert, QuotePicker, DatePicker },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    paymentPlan: {
      type: Object as PropType<IPaymentPlanData | null>,
      default: null
    }
  },
  emits: ['update:dialog', 'created:payment-plan'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const paymentPlanData = ref<IPaymentPlanData>({});

    watch(localDialog, value => {
      if (value) {
        clearErrors();
        paymentPlanData.value = !props.isEdit
          ? ({} as IPaymentPlanData)
          : cloneDeep(props.paymentPlan!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} payment plan information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updatePaymentPlan,
      reset: updateReset
    } = useUpdatePaymentPlan();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createPaymentPlan,
      reset: createReset
    } = useCreatePaymentPlan();

    function updateCustomerInfo(quote: IQuote) {
      clearErrors();
      paymentPlanData.value.customer_name = quote.customer!.name;
      paymentPlanData.value.customer_number = quote.customer!.identifier;
    }

    const localError = ref<ISystemError | null>(null);

    const loading = computed(
      () => isUpdatePending.value || isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = updateError.value || createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    async function save() {
      clearErrors();
      const valid = await formRef.value!.validate();

      if (valid) {
        let paymentPlanInstance: IPaymentPlan | null = null;

        try {
          // for query optimization, we set customer_name and customer_number to the payment plan object
          if (typeof paymentPlanData.value.quote == 'object' && !props.isEdit) {
            paymentPlanData.value.quote = paymentPlanData.value.quote.id;
          }

          paymentPlanInstance = new PaymentPlan(paymentPlanData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (paymentPlanInstance != null) {
          const saveAction = props.isEdit
            ? updatePaymentPlan
            : createPaymentPlan;

          let payload = paymentPlanInstance;
          if (props.isEdit) {
            payload = diffDeep(paymentPlanInstance, props.paymentPlan, true);
          }

          saveAction(payload, {
            onSuccess: paymentPlan => {
              if (!props.isEdit) {
                emit('created:payment-plan', paymentPlan);
              }
              closeDialog();
            }
          });
        }
      }
    }

    return {
      QuoteState,
      localDialog,
      paymentPlanData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      requiredValidator,
      updateCustomerInfo,
      loading,
      error
    };
  }
});
